import React, { FunctionComponent, useRef } from "react";
import { useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Box, Button } from "@mui/material";

import {
  uploadDocumentToS3,
  RecDocumentUploaded,
  createRecDocument,
  deleteRecDocument,
} from "api";
import { DeleteDialog, useDialogControls } from "components";

import { useNotify } from "hooks";
import { UploadDeleteSectionProps } from "./types";
import { deleteIconWrapperStyle } from "./styles";

export const UploadDeleteSection: FunctionComponent<
  UploadDeleteSectionProps
> = ({
  isFileUploaded,
  actionsDisabled,
  successMessage,
  deleteTarget,
  deleteTitle,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const notify = useNotify();
  const deleteDialogControls = useDialogControls();

  const { mutateAsync: uploaded } = useMutation(RecDocumentUploaded);
  const { mutate: create } = useMutation(createRecDocument);
  const { mutateAsync: uploadToS3 } = useMutation(uploadDocumentToS3);
  const { mutateAsync: deleteUploadedRecDocument } =
    useMutation(deleteRecDocument);

  const { recId } = useParams();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    create(
      { fileName: file.name, recId: parseInt(recId!, 10) },
      {
        onSuccess: (details) => {
          uploadToS3({ url: details.s3uploadUrl, file })
            .then(() => uploaded({ recId: parseInt(recId!, 10) }))
            .then(() => {
              notify.success(successMessage);
              isFileUploaded(true);
              setTimeout(() => isFileUploaded(false), 2000);
            });
        },
      },
    );
  };

  const onDeleteClick = () => {
    deleteUploadedRecDocument(recId as any, {
      onError: () =>
        notify.error(
          `Some error has happened while deleting ${deleteTarget} documents!`,
        ),
      onSuccess: () => {
        notify.success(`Selected ${deleteTarget} document is deleted!`);
        deleteDialogControls.close();
        isFileUploaded(true);
        setTimeout(() => isFileUploaded(false), 2000);
      },
    });
  };

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <UploadFileIcon
          onClick={() => fileInputRef.current?.click()}
          style={{ cursor: "pointer" }}
        />
        <input
          type="file"
          accept=".pdf"
          style={{ display: "none" }}
          ref={fileInputRef}
          onChange={handleFileChange}
        />
      </Box>

      <Button
        disabled={actionsDisabled}
        onClick={deleteDialogControls.open}
        sx={deleteIconWrapperStyle}
        variant="text"
      >
        <DeleteForeverIcon />
      </Button>

      <DeleteDialog
        isOpen={deleteDialogControls.isOpen}
        onCancel={deleteDialogControls.close}
        onDeleteClick={onDeleteClick}
        target={deleteTarget}
        title={deleteTitle}
      />
    </>
  );
};
